import { css } from '@inter-ikea-kompis/web-component';

import { Text } from '@ingka/common-styles-webc';
import * as colors from '@ingka/variables/colours-css';
import * as tokens from '@ingka/variables/design-tokens';

/**
 * This is the style class for SummaryPage.
 */
export default class SummaryPagePrintStyle {
	/**
	 * Returns styles.
	 *
	 * @param options ...
	 * @param options.isSafari ...
	 * @param options.includesScreenshot ...
	 */
	public static getStyle(options: { isSafari: boolean; includesScreenshot: boolean }): string {
		const safariStyles = css`
			/* Safari doesn't support using vh to target page height in print. Instead
			we set the screenshot to a fixed height that should fit most of the time. */
			.print-header {
				height: auto;
			}

			.screenshot {
				max-height: 120mm;
			}
		`;

		return css`
			${Text.CSS}

			:host {
				display: none;
			}

			.copyright-notice,
			.footer-text,
			.footer-logo {
				white-space: nowrap;
				color: ${colors.colourTextAndIcon3};
				print-color-adjust: exact;
				-webkit-print-color-adjust: exact;
			}

			.copyright-notice {
				position: fixed;
				top: 0;
				left: 100%;
				transform-origin: top left;
				transform: rotate(90deg);
				font-size: ${8 / 16}rem;
			}

			.footer-text {
				position: fixed;
				bottom: 0;
				left: 0.25in;
				font-size: ${12 / 16}rem;
			}

			.footer-logo {
				position: absolute;
				bottom: 0;
				right: 0.25in;
			}

			.footer-logo svg {
				display: block;
				/* stylelint-disable-next-line unit-allowed-list */
				width: 120px;
				/* stylelint-disable-next-line unit-allowed-list */
				height: 48px;
			}

			/* Making the IKEA logo black and white */
			.footer-logo svg path:nth-child(1),
			.footer-logo svg path:nth-child(3) {
				fill: black;
			}
			.footer-logo svg path:nth-child(2) {
				fill: white;
			}

			.title {
				margin: 0;
			}

			.qr-code {
				display: flex;
				flex-shrink: 0;
				width: ${130 / 16}rem;
				flex-direction: column;
			}

			.qr-code-text {
				margin: 0;
				text-align: center;
			}

			.print-header {
				display: flex;
				flex-direction: column;
				break-after: ${options.includesScreenshot ? 'page' : 'auto'};
				/* stylelint-disable-next-line unit-allowed-list */
				height: calc(100vh - 48px - ${tokens.space300});
			}

			.print-header .title {
				color: ${colors.colourTextAndIcon1};
			}

			.print-details {
				display: flex;
				justify-content: space-between;
				margin-top: ${tokens.space150};
				gap: ${tokens.space200};
			}

			.compactPriceWrapper {
				margin-top: ${tokens.space25};
			}

			.compactPrice {
				margin: 0;
			}

			.compactPrice,
			.compactCommercialMessage {
				font-weight: 600;
				margin: 0;
			}

			.designCodeInfo {
				margin-top: ${tokens.space200};
			}

			.screenshot-wrapper {
				width: 100%;
				flex: 1;
			}

			.screenshot {
				width: 100%;
				flex: 1;
				min-height: 0;
				object-fit: contain;
				object-position: top;
				margin-top: ${tokens.space150};
			}

			@media print {
				:host {
					display: initial;
					color: ${colors.colourTextAndIcon1};
				}
			}

			${options.isSafari ? safariStyles : ''}
		`;
	}

	public static getPrintStyle(): string {
		// We need this @page rule to be set in the root document and not the shadow
		// dom of the current component for the browser to pick it up. The style
		// returned by printPrintStyle will be inserted into the DOM before print
		// and removed after print. This prevents "collisions" with other print
		// rules that the planner might have.
		return css`
			@page {
				size: A4 portrait;
				/* Most printers add a 0.25 inch margin to the printed page. Safari
				doesn't have support for the @page rule so we set the side margin to
				0.25 in all browsers to match Safari. We then add the additional 0.5
				inch using standard CSS margin in the parent component to make out the
				full 1 inch required by the IKEA guidelines. We don't care as much about
				the top and bottom margin as browsers will set these differently to fit
				the browser generated page numbers and meta data. For those browsers
				that respect it we set the margin to 0.75 to once again reach the 1
				inch. */
				margin: 0.75in 0.25in;
			}

			@media print {
				html,
				body {
					font-size: 100% !important;
					transform: none !important;
					zoom: 1 !important;
				}
			}
		`;
	}
}
